import React from "react";
import { Autocomplete, Checkbox, Chip, TextField } from "@mui/material";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";

const uncheckedIcon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

/**
 * Shared multi-value autocomplete for roles, permissions, and grant types.
 */
const ChipAutocomplete = ({
  label,
  value = [],
  onChange,
  options = [],
  required = false,
  freeSolo = false,
  loading = false,
  disabled = false,
  helperText = "",
  placeholder = "",
  error = false,
}) => {
  return (
    <Autocomplete
      multiple
      freeSolo={freeSolo}
      disableCloseOnSelect
      size="small"
      options={options}
      value={value}
      loading={loading}
      disabled={disabled}
      isOptionEqualToValue={(option, selected) => String(option) === String(selected)}
      onChange={(_, next) => {
        const values = (Array.isArray(next) ? next : [])
          .map(String)
          .filter(Boolean);
        // When freeSolo is off, ignore any value that is not in options.
        const accepted = freeSolo
          ? values
          : values.filter((item) =>
              (options || []).some((option) => String(option) === item)
            );
        onChange?.(accepted);
      }}
      // Prevent Enter from creating a free-text chip when freeSolo is disabled.
      onKeyDown={(event) => {
        if (!freeSolo && event.key === "Enter") {
          event.preventDefault();
        }
      }}
      renderOption={(props, option, { selected }) => {
        const { key, ...liProps } = props;
        return (
          <li key={key ?? String(option)} {...liProps}>
            <Checkbox
              icon={uncheckedIcon}
              checkedIcon={checkedIcon}
              checked={selected}
              size="small"
              tabIndex={-1}
              disableRipple
              sx={{ mr: 1, pointerEvents: "none" }}
            />
            {option}
          </li>
        );
      }}
      renderTags={(selected, getTagProps) =>
        selected.map((option, index) => (
          <Chip {...getTagProps({ index })} key={option} label={option} size="small" />
        ))
      }
      componentsProps={{
        popper: {
          sx: { zIndex: 2000 },
        },
      }}
      ListboxProps={{
        style: { maxHeight: 240 },
        onMouseDown: (event) => event.preventDefault(),
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          required={required}
          size="small"
          error={error}
          placeholder={value.length ? "" : placeholder}
          helperText={helperText}
          InputLabelProps={{
            ...params.InputLabelProps,
            shrink: Boolean(value.length) || params.InputLabelProps?.shrink,
          }}
          inputProps={{
            ...params.inputProps,
            required: false,
          }}
        />
      )}
    />
  );
};

export default ChipAutocomplete;
